/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useCallback, useEffect, useRef, useState } from "react"
import Plot from "react-plotly.js";
import Plotly from 'plotly.js-dist-min'
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ChooseDoeParamsWindow from "./ChooseDoeParamsWindow";
import ArgosMenuItem from "../../../../../../../components/Form/Select/ArgosMenuItem";
import Loader from "../../../../../../../components/Loader/Loader";
import AxisDropdown from "./AxisDropdown";
import zIndex from "@mui/material/styles/zIndex";
import ArgosButton from "../../../../../../../components/Buttons/ArgosButton";
import { Checkbox, IconButton, ListItemText, Select, Tooltip } from "@mui/material";
import { ArgosEditIcon, ArgosReloadIcon } from "../../../../../../../components/Icons/ArgosActionIcons";
import { tableButtonGray } from "../../../../../../../../constants/colors";

import VAR_COMMON_CONSTANTS from "../../../../../../../../constants/globalVariables"
import useSocket from "../../../../../../../components/Socket/useSocket";
import useGetHubName from "../../../../../../../components/Socket/useGetHubName";
import { convertDoeToReadableName, defaultXLabel, defaultYLabel, getDoeValuesListFromCoordinate } from "./constants";
import { ModelUsageComponent } from "../../../../../../ScenarioAnalysis/ViewVariables/VariablesTable"
import { usePostRequest } from "../../../../../../../../api/helper";
import { SCENARIO_ANALYSIS_CONSTANTS } from "../../../../../../ScenarioAnalysis/constants";
import {calculateRangeWithBuffer, getMaxFinite} from "../../../../../../../../helpers/graphs";


/**
 * Right panel of the analysis results component.
 * Handles displaying plot, selection of plot axes and tooltip parameters.
 * 
 * @component
 * @param {Array} props.objectsToPlot - List of objects to plot.
 * @param {Object} props.objectToLineTypeMap - Mapping of objects to their line types (ex: redline).
 * @param {Function} props.fetchData - Function to fetch data for plotting.
 * @param {boolean} props.isLoadingRawData - If raw data is being loaded, show yellow spinning icon.
 * @param {boolean} props.errorFetchingRawData - Flag indicating if there was an error fetching raw data.
 * @param {uuidv4} props.loadRawDatasetsData - Unique ID representing the raw data load.
 * @param {boolean} props.isMerged - Flag indicating if is merged dataset.
 * @param {boolean} props.isNewReportGraph - Flag indicating if the graph is in new report creation window.
 * @param {boolean} props.allowUsingDoeVarsAsAxes - If true, show axes dropdowns. 
 * 
 * @returns {JSX.Element} The rendered component.
 */
const AnalysisResultsRightPanel = (props) => {
  const [isUpdatingPlot, setIsUpdatingPlot] = useState(false); // Show loader ontop graph when updating doe, axes, etc
  const [plottingList, setPlottingList] = useState([]);

  // Axes Dropdown Logic
  const [selectedXAxis, setSelectedXAxis] = useState({
    variable_name: defaultXLabel.variable_name,
    display_name: defaultXLabel.display_name
  });
  const [selectedYAxis, setSelectedYAxis] = useState({
    variable_name: defaultYLabel.variable_name,
    display_name: defaultYLabel.display_name
  })
  const handleXAxisChange = (doe_var) => {
    setSelectedXAxis({
      variable_name: doe_var, display_name: doeNameMap.get(doe_var)
    });
  };
  const handleYAxisChange = (doe_var) => {
    setSelectedYAxis({
      variable_name: doe_var, display_name: doeNameMap.get(doe_var)
    });
  };
  const isDefaultAxes = () => selectedXAxis.variable_name == defaultXLabel.variable_name && 
      selectedYAxis.variable_name == defaultYLabel.variable_name

  const [isDOEParamWindowOpen, setIsDOEParamWindowOpen] = useState(false)
  const [selectedTooltipDOE, setSelectedTooltipDOE] = useState([]); // List of user-selected DOE parameters (variable name) for the tooltip
  const [variablesTableData, setVariablesTableData] = useState([]) // List of all doe variable names
  const [doeNameMap, setDoeValToDisplayNameMap] = useState(new Map()) // { DOE variable name : display name}
  const [updatePlotWithObjectsButtonEnabled, setUpdatePlotWithObjectsButtonEnabled] = useState(false);
  const [loadedObjects, setLoadedObjects] = useState(props.objectsToPlot); // List of objects already loaded in the plot
  const fetchVariablesTableHandle = usePostRequest(SCENARIO_ANALYSIS_CONSTANTS.viewVariables.endpoints.variablesTable)

  /**
   * Based on the loaded dataset(s) and objects, returns a list of DOE (Design of Experiments) names 
   * that will be selectable from the axes dropdown and tooltip menu.
   *
   * @returns {Array} An array of DOE names (string) if available, otherwise an empty array.
   */
  const getSelectableDOE = () => {
    if (!props.objectsToPlot || !props.objectToLineTypeMap) return [] // No objects to plot

    let objects = Object.values(props.objectsToPlot)
    let lineTypes = Object.values(props.objectToLineTypeMap)
    if (objects.length > 0 && lineTypes.length > 0) {
      let lineType = Object.keys(Object.values(props.objectToLineTypeMap)[0])
      const lineTypeData = props.objectToLineTypeMap[objects[0]]?.[lineType[0]];
      if (!lineTypeData) return [];
      const { x, y, doe_names } = lineTypeData;
      if (doe_names && doe_names.length > 0) {
        return doe_names
      }
    }
    return []
  }

  /**
   * Fetches DOE variable and display names (ex: doe.variable.name : Doe Variable Name)
   * 
   * @async
   * @function fetchVariablesTable
   * @returns {Promise<void>} A promise that resolves when the fetch operation is complete.
   */
  const fetchVariablesTable = async () => {
    fetchVariablesTableHandle().then((data) => {
      setVariablesTableData(data.table_data)
      setDoeMap(getSelectableDOE(), data.table_data)
    }).catch((e) => {
      console.error(e)
    })
  }

  /**
   * Maps DOE value variable names to their readable names. 
   * Ex: "doe.variable.name" -> "Doe Variable Name"
   *
   * @param {Array} doeValues - An array of DOE values to be mapped.
   * @param {Array} variablesTableData - An array of variable data used to convert DOE values to readable names.
   */
  const setDoeMap = (doeValues, variablesTableData) => {
    doeValues.forEach((value) => {
      setDoeValToDisplayNameMap((prevMap) =>
        new Map(prevMap.set(value, convertDoeToReadableName(value, variablesTableData))));
    })
  }

  /**
   * Set min and max ranges for plot, trying to ensure the same range to achieve a square plot
   */
  const [globalMin, setGlobalMin] = useState(null);
  const [globalMax, setGlobalMax] = useState(null);

  /**
   * Processes objects to plot and their corresponding line types to show on a single plot.
   */
  const mergeDataForPlotting = () => {
    const { objectsToPlot = [], objectToLineTypeMap = {} } = props;
    let localPlottingList = [];
    let max = null;

    const getAbsoluteMax = (x, y) => {
      const absX = x.map(Math.abs);
      const absY = y.map(Math.abs);
      return getMaxFinite([...absX, ...absY]);
    };
    objectsToPlot.forEach(object => {
      selectedLineTypeObject.forEach(lineType => {
        const lineTypeData = objectToLineTypeMap[object]?.[lineType];
        if (!lineTypeData) return;
        let { x, y, doe, doe_names } = lineTypeData;
        // If x and y are not the default x and y axes, don't plot redline data.
        if (lineType === 'redline' && !isDefaultAxes){
          x = [];
          y = [];
        }
        const absoluteMax = getAbsoluteMax(x, y);
        if (max === null || absoluteMax > max) max = absoluteMax;

        // For each coordinate, add the DOE values as an array to the tooltip text
        let doe_text_arr = []
        if (doe && doe.length > 0 && doeNameMap.size > 0) {
          doe = doe.filter((d) => selectedTooltipDOE.includes(d.doe_name))
          y.forEach((element, index) => {
            let names = doe.map((d) => {
              return "<br>" + doeNameMap.get(d.doe_name) + ": " + + d.doe_values[index]?.toFixed(3);
            });
            if (names.length > 0) {
              doe_text_arr.push(names.toString());
            }
          })
        }

        let config = {
          x: x,
          y: y,
          text: "",
          hovertemplate: '(%{x}, %{y})',
          type: "scattergl", // Use scattergl for WebGL rendering
          mode: lineType === 'redline' && isDefaultAxes() ? 'lines' : 'markers',
          name: lineType === 'redline' && isDefaultAxes() ? 'Redline' : 'Raw',
          legendrank: 1,
          marker: { color: 'red' },
        }
        if (doe_text_arr.length > 0) {
          config.text = doe_text_arr
          config.hovertemplate = '(%{x}, %{y}) %{text}'
        }
        localPlottingList.push(config);
      });
    });
    setGlobalMin(-max);
    setGlobalMax(max);
    setPlottingList(localPlottingList);
    setIsUpdatingPlot(false);
  };

  /**
   *
   * Select or remove a line object from list
   */
  const [selectedLineTypeObject, setSelectedLineTypeObject] = React.useState([]) // Data for the linetype object
  const handleSelectedLine = (line) => {
    const _selectedLineTypeObject = [...selectedLineTypeObject]
    const lineIndex = _selectedLineTypeObject.indexOf(line)
    if (lineIndex != -1) {
      _selectedLineTypeObject.splice(lineIndex, 1)
    } else {
      _selectedLineTypeObject.push(line)
    }
    setSelectedLineTypeObject(_selectedLineTypeObject)
  }

  useEffect(() => {
    fetchVariablesTable() // Before loading redline data, fetch doe variable names
  }, [])

  useEffect(() => { // Handle updating the plot
    if (props?.objectsToPlot.length != 0) {
      if (!props?.objectsToPlot.every(o => loadedObjects.includes(o))) {
        setUpdatePlotWithObjectsButtonEnabled(true)
      } else {
        setUpdatePlotWithObjectsButtonEnabled(false)
      }
      mergeDataForPlotting()
    }
    setIsUpdatingPlot(false)
  }, [props?.objectsToPlot, selectedLineTypeObject, props?.doeTableData, props?.isLoading])

  useEffect(() => {
    if (props?.objectsToPlot.length === 0) {
      setSelectedLineTypeObject([])
      setPlottingList([])
      setIsUpdatingPlot(false)
    }
  }, [props?.objectsToPlot.length])

  const didMount = useRef(false);
  useEffect(() => { // Handle sending the request to fetch data based on changes made by user
    if (!didMount.current) {
      didMount.current = true; // Mark as mounted after first run
      return; // Skip effect on initial mount
    }
    setIsUpdatingPlot(true)
    props.fetchData && props.fetchData(
      hasFetchedRawData, false, selectedTooltipDOE,
      selectedXAxis.variable_name, selectedYAxis.variable_name, props?.objectsToPlot)
  }, [selectedTooltipDOE, selectedXAxis, selectedYAxis])

  const useForceUpdate = () => {
    const [value, setValue] = useState(0);
    return () => setValue(value => value + 1);
  }
  const forceUpdate = useForceUpdate();
  const handleClick = () => {
    forceUpdate();
  }
  const handleClickShowDoeParams = () => {
    setIsDOEParamWindowOpen(!isDOEParamWindowOpen)
  }

  const config = {
    modeBarButtonsToAdd: [
      {
        name: 'Square Plot',
        icon: {
          width: 80,
          height: 80,
          path: 'M78,22V79H21V22H78m9-9H12V88H87V13ZM68,46.22H31V54H68ZM53,32H45.22V69H53Z',
          transform: 'matrix(1 0 0 1 -10 -10)'
        },
        click: () => handleClick(),
      },
    ]
  };
  if (doeNameMap.size > 0) {
    config.modeBarButtonsToAdd.push({ // Add Edit Doe params button
      name: "Select DOE Params to Show in Tooltip",
      icon: Plotly.Icons.pencil,
      width: 80,
      height: 80,
      click: () => handleClickShowDoeParams()
    })
  }
  const confref = useRef(null)
  confref.current = config

  const [hasFetchedRawData, setHasFetchedRawData] = useState(false)
  useEffect(() => {
    if (props.isLoadingRawData) {
      setHasFetchedRawData(true)
    }
  }, [props.isLoadingRawData]);

  const [loadOfRawDataSocketData, setLoadOfRawDataSocketData] = useState(null)
  /**
   * Socket to receive load raw data progress
   */
  const onMessage = useCallback((msg) => {
    const { loadOfRawDatasetsId: localLoadOfRawDatasetsId } = props.loadRawDatasetsData
    const { loadOfRawDatasetsId: socketLoadOfRawDatasetsId } = msg.data
    if (localLoadOfRawDatasetsId !== socketLoadOfRawDatasetsId) {
      return
    }
    setLoadOfRawDataSocketData(msg.data)
  }, [props.loadRawDatasetsData])
  const hubName = useGetHubName(`loadRawDatasets`)
  useSocket(hubName, onMessage, props.loadRawDatasetsData != null)

  const shouldShow = (!props.isLoadingRawData && hasFetchedRawData) && !props.errorFetchingRawData
  const loadRawDataFraction = (!loadOfRawDataSocketData || loadOfRawDataSocketData?.datasetsProcessed === 0 || loadOfRawDataSocketData?.totalDatasets === 0) ? '' : `${loadOfRawDataSocketData?.datasetsProcessed || 0}/${loadOfRawDataSocketData?.totalDatasets || 0} raw datasets loaded`

  const {minRange, maxRange} = calculateRangeWithBuffer(globalMin, globalMax)

  return (<>
    <ArgosFlexbox vertical style={{ maxHeight: "100%", maxWidth: "100%" }}>
      {doeNameMap.size > 0 &&
        <ChooseDoeParamsWindow
          selectedDOE={selectedTooltipDOE}
          setSelectedDOE={setSelectedTooltipDOE}
          doeNameMap={doeNameMap}
          isOpen={isDOEParamWindowOpen}
          onClose={() => { setIsDOEParamWindowOpen(false) }} />
      }
      <Loader isLoading={isUpdatingPlot} wrapperStyle={{position: "absolute", left: "50%", zIndex: "1000"}}/>
      <ArgosFlexbox>
        <ArgosFlexbox vertical>
          {<Plot
            modeBarButtonsToAdd={config.modeBarButtonsToAdd}
            config={confref.current}
            data={plottingList}
            useResizeHandler={true}
            style={{ height: 500, width: 500, paddingBottom: "3px" }}
            layout={{
              annotations: [
                VAR_COMMON_CONSTANTS.plots.scatterPlot.DEFAULT_ANNOTATION
              ],
              legend: {
                bgcolor: "blue"
              },
              showlegend: false,
              color: '#ff0000',
              paper_bgcolor: '#f2f4f5',
              plot_bgcolor: '#f2f4f5',
              title: `Analysis Results`,
              shapes: { type: 'path', },
              xaxis: {
                title: selectedXAxis.display_name,
                nticks: 6, /* Specifies the maximum number of ticks */
                range: [minRange, maxRange],
              },
              yaxis: {
                title: selectedYAxis.display_name,
                nticks: 6,
                range: [minRange, maxRange],
              },
            }}
          />}
          {/* Redline and Raw Data Checkboxes */}
          <ArgosFlexbox style={{ height: "5%", justifyContent: "center" }}>
            {selectedXAxis.variable_name == defaultXLabel.variable_name && 
              selectedYAxis.variable_name == defaultYLabel.variable_name &&
              <ArgosFlexbox style={{ alignItems: "center" }}>
                <Checkbox
                  checked={selectedLineTypeObject?.indexOf("redline") != -1 && props?.objectsToPlot.length != 0}
                  onChange={() => handleSelectedLine("redline")}
                  disabled={props?.objectsToPlot.length == 0}
                />
                <span>{"Red Line"}</span>
              </ArgosFlexbox>
            }
              <ArgosFlexbox style={{ alignItems: "center" }}>
                {shouldShow &&
                  <>
                    <Checkbox
                      checked={selectedLineTypeObject?.indexOf("rawdataline") != -1 && props?.objectsToPlot.length != 0}
                      onChange={() => handleSelectedLine("rawdataline")}
                      disabled={props?.objectsToPlot.length == 0}
                    />
                    <span>Raw {VAR_COMMON_CONSTANTS.VAR_DATA_SET}</span>
                  </>
                }

              <Tooltip title={'Load Raw Data For Selected Objects'}>
                <IconButton
                  disabled={props.isLoadingRawData}
                  onClick={() => { props.fetchData(true, false, selectedTooltipDOE, selectedXAxis, selectedYAxis, props?.objectsToPlot)}}
                  size="medium"
                >
                  <ArgosReloadIcon
                    style={props.isLoadingRawData ? { color: 'orange' } : { color: tableButtonGray }}
                    sx={props.isLoadingRawData && {
                      animation: "spin 2s linear infinite",
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(360deg)",
                        },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <span>
                {loadRawDataFraction}
              </span>
            </ArgosFlexbox>
          </ArgosFlexbox>
        </ArgosFlexbox>

        { doeNameMap.size > 0 && props.allowUsingDoeVarsAsAxes && 
          <AxisDropdown
            style={{ padding: "10px" }}
            selectedXAxis={selectedXAxis}
            selectedYAxis={selectedYAxis}
            doeNameMap={doeNameMap}
            handleXAxisChange={handleXAxisChange}
            handleYAxisChange={handleYAxisChange}
          />
        }

      </ArgosFlexbox>
    </ArgosFlexbox>
  </>)
}

export { AnalysisResultsRightPanel }
